import apiClient from '../services/apiClient';

export const DEFAULT_CURRENCY = 'AUD';
export const BOOKING_FEE_AUD = 20;
export const FOREIGN_EXCHANGE_SURCHARGE_RATE = 0.05; // 5% default surcharge (fallback only)
export const DEFAULT_FLEXIBLE_TICKET_RATE = 0.20; // 20% default for flexible ticket upgrade

// Surcharge rates and exchange rates are now applied server-side for security.
// Prices returned from API already include markup - no client-side calculation needed.
// (convertAmount function moved below for currency conversion with exchange rates)

// Fetch surcharge rate from server (single source of truth)
export async function getSurchargeRate() {
  try {
    // Check if method exists (defensive programming)
    if (typeof apiClient.getSurchargeSettings !== 'function') {
      // Method not available - using default rate (expected in some configurations)
      return FOREIGN_EXCHANGE_SURCHARGE_RATE;
    }
    
    const settings = await apiClient.getSurchargeSettings();
    return settings.enabled ? settings.rate : 0;
  } catch (error) {
    // Failed to fetch surcharge settings, using default rate
    return FOREIGN_EXCHANGE_SURCHARGE_RATE; // Fallback to default
  }
}

// Fetch flexible ticket rate from server (or return default)
export function getFlexibleTicketRate() {
  // Return default rate - flexible ticket pricing is not currently exposed to frontend
  // Admin can configure this via admin portal, but for now we use the default
  return Promise.resolve(DEFAULT_FLEXIBLE_TICKET_RATE);
}

export function formatPrice(amount, currency = DEFAULT_CURRENCY) {
  return `${currency} ${amount.toFixed(2)}`;
}
export const PREFERRED_CURRENCY_STORAGE_KEY = 'airflyPreferredCurrency';

export const CURRENCY_OPTIONS = [
  { code: 'AUD', label: 'AUD' },
  { code: 'USD', label: 'USD' },
  { code: 'INR', label: 'INR' },
];

export const FALLBACK_EXCHANGE_RATES = {
  AUD: 1,
  USD: 0.75,
  INR: 71,
};

export function normalizeCurrencyCode(currencyCode) {
  const normalized = String(currencyCode || DEFAULT_CURRENCY).trim().toUpperCase();
  return FALLBACK_EXCHANGE_RATES[normalized] ? normalized : DEFAULT_CURRENCY;
}

export function resolveExchangeRates(liveRates = null) {
  const resolved = {
    ...FALLBACK_EXCHANGE_RATES,
    ...(liveRates && typeof liveRates === 'object' ? liveRates : {}),
  };

  resolved.AUD = 1;
  return resolved;
}

export function convertAmount(amount, fromCurrency = DEFAULT_CURRENCY, toCurrency = DEFAULT_CURRENCY, exchangeRates = null) {
  const numericAmount = Number(amount || 0);
  if (!Number.isFinite(numericAmount)) {
    return 0;
  }

  const fromCode = normalizeCurrencyCode(fromCurrency);
  const toCode = normalizeCurrencyCode(toCurrency);
  const rates = resolveExchangeRates(exchangeRates);

  if (fromCode === toCode) {
    return numericAmount;
  }

  const fromRate = Number(rates[fromCode] || FALLBACK_EXCHANGE_RATES[fromCode] || 1);
  const toRate = Number(rates[toCode] || FALLBACK_EXCHANGE_RATES[toCode] || 1);
  const amountInAud = fromCode === 'AUD' ? numericAmount : numericAmount / fromRate;
  return toCode === 'AUD' ? amountInAud : amountInAud * toRate;
}

export function shouldApplyForeignExchangeSurcharge(fromCurrency = DEFAULT_CURRENCY, toCurrency = DEFAULT_CURRENCY) {
  return normalizeCurrencyCode(fromCurrency) !== normalizeCurrencyCode(toCurrency);
}

export function applyForeignExchangeSurcharge(amount, rate = FOREIGN_EXCHANGE_SURCHARGE_RATE) {
  const numericAmount = Number(amount || 0);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return 0;
  }

  return numericAmount * rate;
}

export function convertAmountWithForeignExchangeCharge(
  amount,
  fromCurrency = DEFAULT_CURRENCY,
  toCurrency = DEFAULT_CURRENCY,
  exchangeRates = null,
) {
  const convertedAmount = convertAmount(amount, fromCurrency, toCurrency, exchangeRates);
  return convertedAmount + applyForeignExchangeSurcharge(convertedAmount);
}

export function formatCurrency(amount, currencyCode = DEFAULT_CURRENCY) {
  const normalized = normalizeCurrencyCode(currencyCode);
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: normalized,
    maximumFractionDigits: 2,
  }).format(Number(amount || 0));
}

export function formatCurrencyParts(amount, currencyCode = DEFAULT_CURRENCY) {
  const normalized = normalizeCurrencyCode(currencyCode);
  const parts = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: normalized,
    maximumFractionDigits: 2,
    currencyDisplay: 'code',
  }).formatToParts(Number(amount || 0));

  const currency = parts
    .filter((part) => part.type === 'currency')
    .map((part) => part.value)
    .join('')
    .trim() || normalized;

  const amountText = parts
    .filter((part) => part.type !== 'currency' && part.type !== 'literal')
    .map((part) => part.value)
    .join('')
    .trim() || '0.00';

  return {
    currency,
    amount: amountText,
    value: `${currency} ${amountText}`,
  };
}

function getLegPriceBreakdown(flight) {
  const breakdown = flight?.priceBreakdown || {};
  const currency = normalizeCurrencyCode(breakdown.currency || DEFAULT_CURRENCY);
  const grandTotal = Number(breakdown.grandTotal ?? flight?.price ?? 0);
  const providerFees = Number(breakdown.providerFees ?? 0);
  const baseFare = Number(breakdown.baseFare ?? Math.max(grandTotal - providerFees, 0));
  const taxes = Number(breakdown.taxes ?? Math.max(grandTotal - baseFare - providerFees, 0));

  return {
    currency,
    baseFare: Number.isFinite(baseFare) ? baseFare : 0,
    taxes: Number.isFinite(taxes) ? taxes : 0,
    providerFees: Number.isFinite(providerFees) ? providerFees : 0,
    grandTotal: Number.isFinite(grandTotal) ? grandTotal : 0,
  };
}

function toAud(amount, currencyCode) {
  return convertAmount(amount, currencyCode, 'AUD');
}

function fromAud(amount, currencyCode, exchangeRates) {
  return convertAmount(amount, 'AUD', currencyCode, exchangeRates);
}

export function getFareSummary({
  flight,
  returnFlight = null,
  passengerCount = 1,
  promoDiscountAmount = 0,
  displayCurrency = DEFAULT_CURRENCY,
  exchangeRates = null,
  surchargeRate = FOREIGN_EXCHANGE_SURCHARGE_RATE,
}) {
  const outbound = getLegPriceBreakdown(flight);
  const inbound = returnFlight ? getLegPriceBreakdown(returnFlight) : getLegPriceBreakdown(null);
  const resolvedPassengerCount = Math.max(Number(passengerCount) || 1, 1);
  const promoSourceCurrency = outbound.currency || inbound.currency || DEFAULT_CURRENCY;
  const displayCode = normalizeCurrencyCode(displayCurrency);

  const outboundPerPassengerAud = toAud(outbound.grandTotal, outbound.currency);
  const inboundPerPassengerAud = toAud(inbound.grandTotal, inbound.currency);
  let subtotalAud = (outboundPerPassengerAud + inboundPerPassengerAud) * resolvedPassengerCount;
  let baseFareAud = (toAud(outbound.baseFare, outbound.currency) + toAud(inbound.baseFare, inbound.currency)) * resolvedPassengerCount;
  let amadeusTaxesAud = (toAud(outbound.taxes, outbound.currency) + toAud(inbound.taxes, inbound.currency)) * resolvedPassengerCount;
  let providerFeesAud = (toAud(outbound.providerFees, outbound.currency) + toAud(inbound.providerFees, inbound.currency)) * resolvedPassengerCount;
  const promoDiscountAud = toAud(promoDiscountAmount, promoSourceCurrency);
  const bookingFeeAud = BOOKING_FEE_AUD;
  
  // Distribute the configured surcharge across visible fare components.
  const surchargeAud = subtotalAud * surchargeRate;
  const totalBeforeSurcharge = baseFareAud + amadeusTaxesAud + providerFeesAud;
  
  let baseFareSurchargeAud = 0;
  let taxesSurchargeAud = 0;
  let feesSurchargeAud = 0;
  
  if (totalBeforeSurcharge > 0) {
    baseFareSurchargeAud = (baseFareAud / totalBeforeSurcharge) * surchargeAud;
    taxesSurchargeAud = (amadeusTaxesAud / totalBeforeSurcharge) * surchargeAud;
    feesSurchargeAud = surchargeAud - baseFareSurchargeAud - taxesSurchargeAud;
  }
  
  baseFareAud += baseFareSurchargeAud;
  amadeusTaxesAud += taxesSurchargeAud;
  providerFeesAud += feesSurchargeAud;
  
  const adjustedSubtotalAud = baseFareAud + amadeusTaxesAud + providerFeesAud;
  const effectivePromoDiscountAud = Math.max(Math.min(promoDiscountAud, adjustedSubtotalAud), 0);
  const discountedAdjustedSubtotalAud = Math.max(adjustedSubtotalAud - effectivePromoDiscountAud, 0);
  const totalAud = discountedAdjustedSubtotalAud + bookingFeeAud;

  return {
    passengers: resolvedPassengerCount,
    displayCurrency: displayCode,
    sourceCurrency: promoSourceCurrency,
    outboundPerPassenger: fromAud(outboundPerPassengerAud * (1 + surchargeRate), displayCode, exchangeRates),
    inboundPerPassenger: fromAud(inboundPerPassengerAud * (1 + surchargeRate), displayCode, exchangeRates),
    subtotal: fromAud(discountedAdjustedSubtotalAud, displayCode, exchangeRates),
    baseFareSubtotal: fromAud(baseFareAud, displayCode, exchangeRates),
    amadeusTaxes: fromAud(amadeusTaxesAud, displayCode, exchangeRates),
    providerFees: fromAud(providerFeesAud, displayCode, exchangeRates),
    promoDiscount: fromAud(effectivePromoDiscountAud, displayCode, exchangeRates),
    bookingFee: fromAud(bookingFeeAud, displayCode, exchangeRates),
    foreignExchangeCharge: 0,
    total: fromAud(totalAud, displayCode, exchangeRates),
  };
}